import { SEO } from "../components/common/SEO";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";
import { useLanguage } from "../context/LanguageProvider";
import { useSiteData } from "../context/SiteDataProvider";
import { pick, UI_STRINGS } from "../lib/i18n";

export default function Process() {
    const { lang } = useLanguage();
    const { siteContent, faqs } = useSiteData();
    const process = siteContent?.process || {};
    const seo = siteContent?.seo?.process || {};

    return (
        <div data-testid="page-process" className="bg-ink">
            <SEO
                title={pick(seo.title, lang) || "Première consultation"}
                description={pick(seo.description, lang)}
                lang={lang}
            />

            <section className="mx-auto max-w-7xl px-5 lg:px-10 py-16 md:py-20">
                <div className="max-w-3xl">
                    <div className="eyebrow mb-3">
                        {pick(UI_STRINGS.sections.first_consult, lang)}
                    </div>
                    <h1 className="font-serif text-4xl md:text-5xl leading-tight text-neutral-50">
                        {pick(process.title, lang)}
                    </h1>
                    <p className="mt-6 text-neutral-300 text-lg leading-relaxed">
                        {pick(process.intro, lang)}
                    </p>
                </div>

                <ol className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
                    {(process.steps || []).map((s, i) => (
                        <li
                            key={i}
                            className="card-line p-8 flex gap-5"
                            data-testid={`process-step-${i}`}
                        >
                            <div className="font-serif text-4xl text-gold shrink-0 leading-none">
                                {String(i + 1).padStart(2, "0")}
                            </div>
                            <div>
                                <h3 className="font-serif text-xl text-neutral-50 mb-2">
                                    {pick(s.title, lang)}
                                </h3>
                                <p className="text-sm text-neutral-300 leading-relaxed">
                                    {pick(s.text, lang)}
                                </p>
                            </div>
                        </li>
                    ))}
                </ol>
            </section>

            {/* FAQ */}
            <section
                className="bg-ink-deep border-t border-ink-border py-20 md:py-24"
                data-testid="process-faq"
            >
                <div className="mx-auto max-w-4xl px-5 lg:px-10">
                    <div className="mb-12 max-w-2xl">
                        <div className="eyebrow mb-3">
                            {pick(UI_STRINGS.sections.faq, lang)}
                        </div>
                        <h2 className="font-serif text-3xl md:text-4xl text-neutral-50 leading-tight">
                            {pick(
                                {
                                    fr: "Les réponses aux questions les plus courantes",
                                    en: "Answers to the most frequent questions",
                                },
                                lang,
                            )}
                        </h2>
                    </div>
                    <Accordion
                        type="single"
                        collapsible
                        className="w-full"
                        data-testid="faq-accordion"
                    >
                        {faqs.map((faq) => (
                            <AccordionItem
                                key={faq.id}
                                value={faq.id}
                                className="border-b border-ink-border"
                                data-testid={`faq-item-${faq.order}`}
                            >
                                <AccordionTrigger className="text-left font-serif text-lg md:text-xl text-neutral-50 hover:text-gold py-5 [&_svg]:text-gold [&[data-state=open]]:text-gold">
                                    {pick(faq.question, lang)}
                                </AccordionTrigger>
                                <AccordionContent className="text-neutral-300 text-base leading-relaxed pb-6">
                                    {pick(faq.answer, lang)}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>
        </div>
    );
}
